import { PlusIcon } from "@phosphor-icons/react/dist/csr/Plus";
import { useMemo, useState } from "react";
import { Alert } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { PageContainer } from "@volca/ui/components/ui/page-components";
import {
  SimpleSidebarContent,
  SimpleSidebarLayout,
} from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { getPayrollRosterApiErrorMessage } from "@/payroll/roster/api-errors";
import { ImportValuesDialog } from "@/payroll/roster/import-values-dialog";
import { RosterDisplayControls } from "@/payroll/roster/roster-display-controls";
import { RosterMemberSummary } from "@/payroll/roster/roster-member-summary";
import { RosterMoreActionsMenu } from "@/payroll/roster/roster-more-actions-menu";
import { RosterPageHeader } from "@/payroll/roster/roster-page-header";
import type {
  PayrollRoster,
  PayrollRosterRowFocus,
} from "@/payroll/roster/types";
import { usePayrollRosterColumns } from "@/payroll/roster/use-payroll-roster-columns";
import { useRosterFilters } from "@/payroll/roster/use-roster-filters";
import { useRosterRowFocus } from "@/payroll/roster/use-roster-row-focus";
import { filterPayrollRosterDraftsByFocus } from "@/payroll/roster/utils";
import { RosterEditorSidePanel } from "./roster-editor-side-panel";
import { RosterReassignmentAlert } from "./roster-reassignment-alert";
import { RosterTableCard } from "./roster-table-card";
import { RosterUnpublishedChangesBar } from "./roster-unpublished-changes-bar";
import { useRosterEditorActions } from "./use-roster-editor-actions";
import { useRosterEditorData } from "./use-roster-editor-data";

interface PayrollRosterEditorProps {
  roster: PayrollRoster;
  businessId: string;
  canSyncTechnicians?: boolean;
}

export const PayrollRosterEditor = ({
  roster,
  businessId,
  canSyncTechnicians = false,
}: PayrollRosterEditorProps) => {
  const {
    teams,
    contract,
    publishedVersion,
    publishedAt,
    fields,
    draftMembers,
    dismissedExternalIds,
    fieldOptions,
    hasErrors,
    needsReassignment,
    flaggedMembers,
    changes,
    hasChanges,
    editedIds,
    suggestionDrafts,
    displayedDrafts,
    memberCount,
    suggestionCount,
    departedIds,
    departedCount,
  } = useRosterEditorData(roster, businessId);
  const [rowFocus, setRowFocus] = useRosterRowFocus();
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 });
  const [importOpen, setImportOpen] = useState(false);
  const {
    editTarget,
    setEditTarget,
    reviewOpen,
    setReviewOpen,
    dismissingSuggestionId,
    isDismissingSuggestion,
    isSavingDraft,
    publishRoster,
    discardDraft,
    syncTechnicians,
    mutatingRef,
    rosterMutationPending,
    openDraft,
    openAdd,
    closeDrawer,
    saveEdit,
    removeMember,
    dismissSuggestion,
    applyImport,
    openReview,
    publish,
    discard,
    syncUsers,
  } = useRosterEditorActions({
    businessId,
    fields,
    draftMembers,
    suggestionDrafts,
    dismissedExternalIds,
    hasErrors,
    setRowFocus,
  });
  const rosterFilters = useRosterFilters(displayedDrafts, fields, {
    fieldOptions,
    teams,
    businessId,
  });
  const { search, activeFilterCount } = rosterFilters;
  const filteredDrafts = useMemo(
    () =>
      filterPayrollRosterDraftsByFocus(
        rosterFilters.filteredRows,
        rowFocus,
        departedIds,
      ),
    [rosterFilters.filteredRows, rowFocus, departedIds],
  );
  const hasActiveDisplayFilters =
    rosterFilters.hasActiveFilters || rowFocus !== null;
  const scrollResetKey = `${rosterFilters.filterKey}-${rowFocus ?? "all"}-${pagination.pageIndex}-${pagination.pageSize}`;

  const clearDisplayFilters = () => {
    rosterFilters.clearFilters();
    setRowFocus(null);
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  };

  const toggleRowFocus = (focus: Exclude<PayrollRosterRowFocus, null>) => {
    setRowFocus(rowFocus === focus ? null : focus);
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  };

  const { columns, tableMinWidth } = usePayrollRosterColumns({
    fields,
    teams,
    draftMembers: displayedDrafts,
    editedIds,
    flaggedMembers,
    departedIds,
    onOpen: openDraft,
    onDismissSuggestion: dismissSuggestion,
    dismissingSuggestionId,
    busy: rosterMutationPending,
  });

  return (
    <SimpleSidebarLayout
      open={editTarget != null || reviewOpen}
      onOpenChange={(open) => {
        if (mutatingRef.current) return;
        if (!open) {
          setEditTarget(null);
          setReviewOpen(false);
        }
      }}
      sidebarWidth="480px"
      className="overflow-hidden"
    >
      <SimpleSidebarContent removeScrollArea className="flex flex-col">
        <PageContainer variant="workspace">
          <Stack align="start" gap={4} className="min-h-0 w-full flex-1">
            <Stack align="start" gap={1} className="w-full shrink-0">
              <RosterPageHeader
                status={{ publishedVersion }}
                actions={
                  <>
                    <Button
                      variant="secondary"
                      disabled={rosterMutationPending}
                      onClick={openAdd}
                    >
                      <PlusIcon data-icon="inline-start" />
                      Add person
                    </Button>
                    <RosterMoreActionsMenu
                      onImportValues={() => setImportOpen(true)}
                      canImportValues={fields.length > 0}
                      onSyncTechnicians={
                        canSyncTechnicians ? syncUsers : undefined
                      }
                      isSyncing={syncTechnicians.isPending}
                      busy={rosterMutationPending}
                    />
                  </>
                }
              />
              <RosterMemberSummary
                memberCount={memberCount}
                displayedCount={displayedDrafts.length}
                filteredCount={filteredDrafts.length}
                suggestionCount={suggestionCount}
                departedCount={departedCount}
                focus={rowFocus}
                hasActiveFilters={hasActiveDisplayFilters}
                publishedAt={publishedAt}
                onFocusChange={toggleRowFocus}
              />
            </Stack>

            {syncTechnicians.isError && (
              <Alert color="danger" className="shrink-0">
                ServiceTitan technicians could not be synced.{" "}
                {getPayrollRosterApiErrorMessage(syncTechnicians.error)}
              </Alert>
            )}

            {needsReassignment.length > 0 && (
              <RosterReassignmentAlert count={needsReassignment.length} />
            )}

            {hasChanges && (
              <RosterUnpublishedChangesBar
                changeCount={changes.length}
                busy={rosterMutationPending}
                discarding={discardDraft.isPending}
                onDiscard={discard}
                onReview={openReview}
              />
            )}

            <RosterTableCard
              controls={
                <RosterDisplayControls
                  {...rosterFilters.controls}
                  onSearchChange={(value) => {
                    rosterFilters.controls.onSearchChange(value);
                    setPagination((current) => ({ ...current, pageIndex: 0 }));
                  }}
                  onFiltersChange={(filters) => {
                    rosterFilters.controls.onFiltersChange(filters);
                    setPagination((current) => ({ ...current, pageIndex: 0 }));
                  }}
                />
              }
              displayedCount={displayedDrafts.length}
              filteredDrafts={filteredDrafts}
              columns={columns}
              tableMinWidth={tableMinWidth}
              pagination={pagination}
              onPaginationChange={setPagination}
              flaggedMembers={flaggedMembers}
              departedIds={departedIds}
              onRowClick={rosterMutationPending ? undefined : openDraft}
              scrollResetKey={scrollResetKey}
              search={search}
              activeFilterCount={
                activeFilterCount + (rowFocus === null ? 0 : 1)
              }
              onClearFilters={clearDisplayFilters}
            />
          </Stack>
        </PageContainer>
      </SimpleSidebarContent>

      <RosterEditorSidePanel
        businessId={businessId}
        teams={teams}
        fields={fields}
        fieldOptions={fieldOptions}
        contract={contract}
        editTarget={editTarget}
        draftMembers={draftMembers}
        suggestionDrafts={suggestionDrafts}
        reviewOpen={reviewOpen}
        changes={changes}
        isSavingDraft={isSavingDraft}
        isDismissingSuggestion={isDismissingSuggestion}
        publishing={publishRoster.isPending}
        onSave={saveEdit}
        onCancel={closeDrawer}
        onRemove={removeMember}
        onDismissSuggestion={dismissSuggestion}
        onCloseReview={() => setReviewOpen(false)}
        onPublish={publish}
      />

      <ImportValuesDialog
        open={importOpen}
        fields={fields}
        saving={isSavingDraft}
        onOpenChange={setImportOpen}
        onApply={applyImport}
      />
    </SimpleSidebarLayout>
  );
};
